import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  ICreateReleaseNotePayload,
  IReleaseNote,
  ITranslateReleaseNotePayload,
  IUpdateReleaseNotePayload,
  ReleaseNoteAudience,
  ReleaseNotePublishStatus,
} from "../../interfaces/releaseNote.interface";
import {
  createReleaseNoteServer,
  deleteReleaseNoteServer,
  getReleaseNoteServer,
  getReleaseNotesServer,
  publishReleaseNoteServer,
  translateReleaseNoteServer,
  unpublishReleaseNoteServer,
  updateReleaseNoteServer,
} from "./releaseNotesApi";

export interface ReleaseNotesState {
  releaseNotes: IReleaseNote[];
  currentReleaseNote: IReleaseNote | null;
  status: "idle" | "loading" | "failed";
}

const initialState: ReleaseNotesState = {
  releaseNotes: [],
  currentReleaseNote: null,
  status: "idle",
};

export const getReleaseNotesThunk = createAsyncThunk(
  "releaseNotes/getAll",
  async (params: { status?: ReleaseNotePublishStatus; search?: string }) => {
    return getReleaseNotesServer(params);
  }
);

export const getReleaseNoteThunk = createAsyncThunk(
  "releaseNotes/getOne",
  async (id: string) => {
    return getReleaseNoteServer(id);
  }
);

export const createReleaseNoteThunk = createAsyncThunk(
  "releaseNotes/create",
  async (payload: ICreateReleaseNotePayload) => {
    return createReleaseNoteServer(payload);
  }
);

export const updateReleaseNoteThunk = createAsyncThunk(
  "releaseNotes/update",
  async (params: { id: string; payload: IUpdateReleaseNotePayload }) => {
    return updateReleaseNoteServer(params);
  }
);

export const deleteReleaseNoteThunk = createAsyncThunk(
  "releaseNotes/delete",
  async (id: string) => {
    return deleteReleaseNoteServer(id);
  }
);

export const publishReleaseNoteThunk = createAsyncThunk(
  "releaseNotes/publish",
  async (params: { id: string; audiences: ReleaseNoteAudience[] }) => {
    return publishReleaseNoteServer(params);
  }
);

export const unpublishReleaseNoteThunk = createAsyncThunk(
  "releaseNotes/unpublish",
  async (params: { id: string; audiences: ReleaseNoteAudience[] }) => {
    return unpublishReleaseNoteServer(params);
  }
);

export const translateReleaseNoteThunk = createAsyncThunk(
  "releaseNotes/translate",
  async (params: { id: string; payload: ITranslateReleaseNotePayload }) => {
    return translateReleaseNoteServer(params);
  }
);

const upsert = (state: ReleaseNotesState, note: IReleaseNote) => {
  const index = state.releaseNotes.findIndex((n) => n._id === note._id);
  if (index >= 0) {
    state.releaseNotes[index] = note;
  } else {
    state.releaseNotes.unshift(note);
  }
  state.currentReleaseNote = note;
};

export const releaseNotesSlice = createSlice({
  name: "releaseNotes",
  initialState,
  reducers: {
    setCurrentReleaseNote: (
      state,
      action: PayloadAction<IReleaseNote | null>
    ) => {
      state.currentReleaseNote = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReleaseNotesThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getReleaseNotesThunk.fulfilled, (state, action) => {
        state.status = "idle";
        state.releaseNotes = action.payload;
      })
      .addCase(getReleaseNotesThunk.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getReleaseNoteThunk.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(createReleaseNoteThunk.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(updateReleaseNoteThunk.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(publishReleaseNoteThunk.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(unpublishReleaseNoteThunk.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(translateReleaseNoteThunk.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(deleteReleaseNoteThunk.fulfilled, (state, action) => {
        state.releaseNotes = state.releaseNotes.filter(
          (n) => n._id !== action.payload._id
        );
        if (state.currentReleaseNote?._id === action.payload._id) {
          state.currentReleaseNote = null;
        }
      });
  },
});

export const selectReleaseNotes = (state: RootState) =>
  state.releaseNotes.releaseNotes;
export const selectCurrentReleaseNote = (state: RootState) =>
  state.releaseNotes.currentReleaseNote;
export const selectReleaseNotesStatus = (state: RootState) =>
  state.releaseNotes.status;

export const { setCurrentReleaseNote } = releaseNotesSlice.actions;

export default releaseNotesSlice.reducer;
